<template>
	<div style="text-align: center">
		<imdex-spinner width="20" size="2" v-if="isLoading" />
		<div v-if="!isLoading">
			<div class="inputs" style="gap: 5px">
				<div
					@click.prevent="toggleColorOpen"
					class="color-block"
					ref="color-block"
					:style="`background:${
						colors[activeColor] && colors[activeColor].hex
					};width:40px;height:40px;`"
				></div>
				<transition enter-active-class="fadeInUp" leave-active-class="fadeOutDown">
					<ColorPicker
						@close="colorOpen = false"
						:color="activeColorComputed"
						@change="updateColor"
						ref="colorPicker"
						v-if="colorOpen"
						:forceColorUpdate="true"
					/>
				</transition>
				{{ $t(`ssa.displayPane.picksColorPicker.displayColor`) }}
			</div>
		</div>
	</div>
</template>
<script>
import vtkMath from "vtk.js/Sources/Common/Core/Math";
import { defineComponent } from "vue";
import { useActiveStore } from "@/stores/ssaStore/active";
import { usePresetsStore } from "@/stores/ssaStore/presets";
import { useSsaStore } from "@/stores/ssaStore";
import ColorPicker from "./colorPicker.vue";
import { storeToRefs } from "pinia";

export default defineComponent({
	name: "imdex-pick-color",
	props: {
		r: {
			type: Object,
			default: () => ({})
		},
		color: {
			type: Object,
			default: () => {
				return { r: 255, g: 255, b: 255 };
			}
		},
		layerName: {
			type: String,
			default: ""
		},
		defaults_applied: {
			type: Boolean,
			default: false
		}
	},
	setup() {
		const activeStore = useActiveStore();
		const ssaStore = useSsaStore();
		const presetStore = usePresetsStore();
		const { SSA_ACTIVE_LAYER: activeLayer, SSA_ACTIVE_DATASET: activeDataset } = storeToRefs(
			useActiveStore()
		);
		const toAnnotationValue = (n) => {
			return (Math.round(n * 100) / 100).toString();
		};
		return {
			toAnnotationValue,
			ssaStore,
			activeStore,
			activeLayer,
			activeDataset,
			activeView: activeStore.SSA_ACTIVE_VIEW,
			activeLayerState: activeStore.SSA_ACTIVE_LAYER_STATE,
			// activeRepresentationState: storeToRefs(activeStore.SSA_ACTIVE_REPRESENTATION_STATE),
			getColumn: presetStore.SSA_FIELD_COLUMN_GET,
			updateColorBy: ssaStore.SSA_COLOR_BY_UPDATE
		};
	},
	data() {
		return {
			isLoading: true,
			showStopVal: false,
			stopval: 0,
			colorOpen: false,
			activeColor: 0,
			activeMin: 0,
			activeMax: 0,
			colors: [],
			range: [0, 20],
			colorOpenListener: null
		};
	},
	mounted() {
		this.$nextTick(() => {
			if (this.r.coloring.colorRange) {
				if (this.r.coloring.rgbPoints && this.r.coloring.rgbPoints.length) {
					if (this.colors.length == 0) {
						this.init(this.r);
					} else {
						this.loadStateFromRepresentation();
						this.activeMin = this.colors[this.activeColor].min;
						this.activeMax = this.colors[this.activeColor].max;
					}
				}

				const val = this.r.coloring.colorRange;
				if (val[1] < val[0]) return;
				if (val[0] == this.range[0] && val[1] == this.range[1]) {
					return;
				}
				if (this.range[0] != val[0] && this.range[1] != val[1]) {
					this.range = val;
				}
			}
		});
	},
	components: {
		ColorPicker
	},
	methods: {
		init(v) {
			this.isLoading = true;
			if (v == null || v.coloring.colorRange == null) {
				this.isLoading = false;
				return;
			}
			const {
				colorRange: [min, max],
				rgbPoints,
				annotations
			} = v.coloring;
			this.range = [min, max];
			if (this.range == null) {
				this.isLoading = false;
				return;
			}

			if (this.color != null && !this.defaults_applied) {
				this.updateColor({
					color: this.rgbToHex(this.color.r, this.color.g, this.color.b)
				});
			}

			this.colors = [
				{
					hex:
						this.color != null && !this.defaults_applied
							? this.rgbToHex(this.color.r, this.color.g, this.color.b)
							: this.rgbToHex(
									rgbPoints[1] * 255,
									rgbPoints[2] * 255,
									rgbPoints[3] * 255
							  ),
					min: Math.round(rgbPoints[0] * 100) / 100,
					max: Math.round(rgbPoints[4] * 100) / 100,
					label: annotations[1]
				}
			];

			this.activeMin = this.colors[0] ? this.colors[0].min : 0;
			this.activeMax = this.colors[0] ? this.colors[0].max : 1;
			this.updateStore();
			this.isLoading = false;
		},
		toggleColorOpen() {
			this.colorOpen = !this.colorOpen;
			const container = window.document.getElementById("pane-container");
			if (this.colorOpen) {
				this.$nextTick(() => {
					window.addEventListener(
						"mousedown",
						(this.colorOpenListener = (e) => {
							const [x, y] = [e.pageX, e.pageY];
							const elements = document.elementsFromPoint(x, y);
							for (let i = 0; i < elements.length; i++) {
								if (elements[i].classList.contains("color-picker")) {
									return;
								}
							}
							this.colorOpen = false;
							window.removeEventListener("mousedown", this.colorOpenListener);
						})
					);
					const { left, top, width } = this.$refs[`color-block`].getBoundingClientRect();
					const { height: elHeight } = this.$refs.colorPicker.$el.getBoundingClientRect();
					if (left - 250 < 0) {
						this.$refs.colorPicker.$el.style.left = left + width + "px";
					} else {
						this.$refs.colorPicker.$el.style.left = left - 250 + "px";
					}
					if (top + elHeight > window.innerHeight) {
						this.$refs.colorPicker.$el.style.top =
							window.innerHeight - elHeight - 15 + "px";
					} else {
						this.$refs.colorPicker.$el.style.top = top + "px";
					}

					container.appendChild(this.$refs.colorPicker.$el);
				});
			}
		},
		getHexFromRGB(rgb) {
			let { r, g, b } = rgb;

			r = r * 255;
			g = g * 255;
			b = b * 255;

			function rgbToHex(r, g, b) {
				return ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
			}
			return "#" + rgbToHex(r, g, b);
		},
		rgbToHex(r, g, b) {
			return "#" + this.componentToHex(r) + this.componentToHex(g) + this.componentToHex(b);
		},
		componentToHex(c) {
			const hex = c.toString(16);
			return hex.length == 1 ? "0" + hex : hex;
		},
		loadStateFromRepresentation() {
			this.isLoading = true;
			this.activeColor = 0;
			const rgbPoints = this.r && this.r.coloring && this.r.coloring.rgbPoints;
			const numberOfColors = 1;
			if (rgbPoints == null) {
				this.isLoading = false;
				return;
			}
			const colors = [];
			for (let i = 0; i < numberOfColors; i++) {
				const hex = this.getHexFromRGB({
					r: rgbPoints[i * 8 + 1],
					g: rgbPoints[i * 8 + 2],
					b: rgbPoints[i * 8 + 3]
				});
				const obj = {
					hex,
					label: "",
					min: rgbPoints[i * 8],
					max: rgbPoints[i * 8 + 4]
				};
				colors.push(obj);
			}
			this.colors = colors;
			this.isLoading = false;
		},
		updateColor(v) {
			if (v.color != null) {
				this.colors[this.activeColor].hex = v.color;
				this.updateStore();
			}
		},
		addAnnotation(val) {
			if (val.target) {
				this.colors[this.activeColor].label = val.target.value;
			} else {
				this.colors[this.activeColor].label = val;
			}
			this.$nextTick(() => {
				this.updateStore();
			});
		},
		updateStore() {
			this.$nextTick(() => {
				const { view, layerName } = this.r;
				const {
					field,
					colorRange: [min, max]
				} = this.r.coloring;
				const rgbPointsToEdit = [];
				const annotationsToEdit = [];
				let n = 0;

				for (let i = 0; i < this.colors.length; i++) {
					const snap = min + (max - min) / 2;
					annotationsToEdit.push(snap.toString());
					annotationsToEdit.push(this.colors[i].label || "");
					if (n == 0) {
						rgbPointsToEdit[n] = Number(min);
						rgbPointsToEdit[n + 1] = vtkMath.hex2float(this.colors[i].hex)[0];
						rgbPointsToEdit[n + 2] = vtkMath.hex2float(this.colors[i].hex)[1];
						rgbPointsToEdit[n + 3] = vtkMath.hex2float(this.colors[i].hex)[2];
						rgbPointsToEdit[n + 4] = Number(max);
						rgbPointsToEdit[n + 5] = vtkMath.hex2float(this.colors[i].hex)[0];
						rgbPointsToEdit[n + 6] = vtkMath.hex2float(this.colors[i].hex)[1];
						rgbPointsToEdit[n + 7] = vtkMath.hex2float(this.colors[i].hex)[2];
						n += 8;
					} else {
						rgbPointsToEdit[n] = Number(min);
						rgbPointsToEdit[n + 1] = vtkMath.hex2float(this.colors[i].hex)[0];
						rgbPointsToEdit[n + 2] = vtkMath.hex2float(this.colors[i].hex)[1];
						rgbPointsToEdit[n + 3] = vtkMath.hex2float(this.colors[i].hex)[2];
						rgbPointsToEdit[n + 4] = Number(max);
						rgbPointsToEdit[n + 5] = vtkMath.hex2float(this.colors[i].hex)[0];
						rgbPointsToEdit[n + 6] = vtkMath.hex2float(this.colors[i].hex)[1];
						rgbPointsToEdit[n + 7] = vtkMath.hex2float(this.colors[i].hex)[2];
						n += 8;
					}
				}
				const stateToPush = {
					layerName,
					viewId: view,
					field,
					column: this.getColumn(field),
					rgbPoints: rgbPointsToEdit,
					annotations: annotationsToEdit
				};
				this.updateColorBy(stateToPush);
			});
		}
	},
	computed: {
		activeColorComputed() {
			let hex = this.colors[this.activeColor] && this.colors[this.activeColor].hex;
			if (hex == null) {
				hex = "#ff9900";
			}
			return { color: hex };
		}
	},
	watch: {
		activeDataset: {
			deep: true,
			handler() {
				this.loadStateFromRepresentation();
			}
		},
		activeLayer: {
			deep: true,
			handler(v) {
				if (this.layerName == v) {
					this.loadStateFromRepresentation();
				}
			}
		},
		r: {
			deep: true,
			handler(v) {
				this.$nextTick(() => {
					if (v.coloring.colorRange) {
						if (v.coloring.rgbPoints && v.coloring.rgbPoints.length) {
							if (this.colors.length == 0) {
								this.init(v);
							} else {
								this.loadStateFromRepresentation();
								this.activeMin = this.colors[this.activeColor].min;
								this.activeMax = this.colors[this.activeColor].max;
							}
						}

						const val = v.coloring.colorRange;
						if (val[1] < val[0]) return;
						if (val[0] == this.range[0] && val[1] == this.range[1]) {
							return;
						}
						if (this.range[0] != val[0] && this.range[1] != val[1]) {
							this.range = val;
						}
					}
				});
			}
		},
		activeColor(val) {
			this.activeMin = this.colors[val].min;
			this.activeMax = this.colors[val].max;
		}
	}
});
</script>
<style lang="scss" scoped>
.colorPickerContainer {
	position: relative;
}
.colorPicker {
	display: flex;
	align-items: center;
	width: 100%;
	padding: 5px;
	position: relative;
}

.colorPickerWithVisibility {
	display: flex;
	align-items: center;
	width: 100%;
	padding: 5px 0px;
	position: relative;
}

.colorPickerWithVisibility button {
	margin: 0 5px 0 0;
}

.colorPickerColor {
	height: 25px;
	width: 25px;
	min-width: 25px;
	cursor: pointer;
	border: solid 1px #ffffff50;
}
.colorPickerValue {
	font-size: 10px;
	color: #fff;
	display: flex;
	align-items: center;
	padding-left: 10px;
	font-weight: bold;
}
.pickerValue {
	max-width: 100px;
	margin-left: auto;
	margin-left: 10px;
}
.color-block {
	min-width: 40px;
	min-height: 40px;
	max-width: 40px;
	max-height: 40px;
	margin-right: 10px;
	border: solid 1px #808080;
	cursor: pointer;
}

.stopval {
	position: absolute;
	top: -100%;
	opacity: 1;
	pointer-events: none;
	transform: translateX(-50%);

	&.active {
		opacity: 1;
	}
	padding: 0;
	border-radius: 5px;

	font-size: 9pt;
}
.slider {
	height: 10px;
	background: #808080;
	width: 100%;
	position: relative;
	display: flex;

	opacity: 1 !important;
	.slider-item {
		flex: 1;
		opacity: 1 !important;
		position: relative;
		cursor: pointer;

		&:hover {
			box-shadow: 1px 1px 10px rgba(255, 255, 255, 0.5);
		}
		&:nth-child(1) {
			.slider-box {
				display: none;
			}
		}
		&.active {
			box-shadow: 1px 1px 10px rgba(255, 255, 255, 1);
		}
		.slider-box {
			background: #000000;
			cursor: pointer;
			transition: border 0.3s ease;
			z-index: 10;

			&:hover {
				border: solid 1px #ffffff !important;
			}
			height: 25px;
			width: 2px;
			border: solid 1px #ffffff40;
			position: absolute;
			transform: translateY(-50%) translateX(-50%);
			top: 50%;
			left: 0;
		}
	}
}
.inputs {
	display: flex;
	align-items: center;
}
</style>
